import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Modal, Nav, Spinner, Table } from "@themesberg/react-bootstrap";
import { Link } from "react-router-dom";
import { useCookies } from "react-cookie";

import { Routes } from "../../routes";
import { getDashboardPendingOrders } from "../../controller/api/statistic/dashboard";
import { PERMISSION_INDEX, PERMISSION_MODULE, normalizePermission } from "../../helper/permission";

const PENDING_STATUS_LABELS = {
  1: { label: "Chờ tới kỳ in", variant: "warning", text: "dark" },
  2: { label: "Đang xử lý in", variant: "info", text: "white" }
};

const REGION_LABELS = {
  MB: "Miền Bắc",
  MT: "Miền Trung",
  MN: "Miền Nam"
};

const PRODUCT_TABS = [
  { key: "vietlott", label: "Vietlott", permissionIndex: PERMISSION_INDEX[PERMISSION_MODULE.VIETLOTT] },
  { key: "classic", label: "Kiến thiết", permissionIndex: PERMISSION_INDEX[PERMISSION_MODULE.CLASSIC] },
  { key: "loto", label: "Loto", permissionIndex: PERMISSION_INDEX[PERMISSION_MODULE.LOTO] }
];

const money = value => new Intl.NumberFormat("vi-VN").format(Number(value || 0));

const dateTime = value => new Date(value).toLocaleString("vi-VN", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit"
});

const ticketLink = item => {
  if (item.product === "vietlott") return `${Routes.VietlottTicket.path}?ticketId=${item.id}`;
  if (item.product === "classic") return `${Routes.ClassicSearch.path}?search=${item.cartId}`;
  return `${Routes.LotoCart.path}?cartId=${item.cartId}`;
};

const cartLink = item => {
  if (item.product === "vietlott") return `${Routes.VietlottTicket.path}?cartId=${item.cartId}`;
  if (item.product === "classic") return `${Routes.ClassicSearch.path}?search=${item.cartId}`;
  return `${Routes.LotoCart.path}?cartId=${item.cartId}`;
};

const itemLabel = item => (item.product === "classic" ? REGION_LABELS[item.label] || item.label : item.label);

export default ({ show, onHide }) => {
  const [cookies] = useCookies(["lottmekong"]);
  const permission = normalizePermission(localStorage.getItem("permission") || "");
  const tabs = PRODUCT_TABS.filter(tab => permission[tab.permissionIndex] === "1");
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(tabs[0]?.key);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadPendingOrders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getDashboardPendingOrders(cookies.lottmekong);
      if (!response) throw new Error("Dữ liệu trả về không hợp lệ.");
      setData(response);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Không thể tải danh sách chờ xử lý.");
    } finally {
      setLoading(false);
    }
  }, [cookies.lottmekong]);

  useEffect(() => {
    if (!show) return;
    loadPendingOrders();
  }, [show, loadPendingOrders]);

  const group = data?.[activeTab] || { total: 0, items: [] };
  const isTruncated = data && group.total > group.items.length;
  const visibleTotal = data ? tabs.reduce((sum, tab) => sum + (data[tab.key]?.total || 0), 0) : 0;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="h6">
          Vé / đơn chờ xử lý
          {data && <span className="text-muted fw-normal ms-2">Tổng {money(visibleTotal)}</span>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Nav variant="pills" className="mb-3 gap-2">
          {tabs.map(tab => (
            <Nav.Item key={tab.key}>
              <Nav.Link
                active={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="px-3 py-2"
              >
                {tab.label}
                <Badge bg="secondary" className="ms-2">{money(data?.[tab.key]?.total)}</Badge>
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        {error && <Alert variant="danger">{error}</Alert>}

        {tabs.length === 0 ? (
          <div className="text-center text-muted py-5">Tài khoản không có quyền xem sản phẩm nào.</div>
        ) : loading ? (
          <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
        ) : group.items.length === 0 ? (
          <div className="text-center text-muted py-5">Không có vé / đơn nào đang chờ xử lý.</div>
        ) : (
          <>
            {isTruncated && (
              <Alert variant="warning" className="py-2 small">
                Đang hiển thị {money(group.items.length)} bản ghi mới nhất trên tổng {money(group.total)}.
              </Alert>
            )}
            <Table responsive hover className="align-items-center table-flush">
              <thead className="thead-light">
                <tr>
                  <th className="border-0">Mã vé / đơn</th>
                  <th className="border-0">Mã đơn hàng</th>
                  <th className="border-0">Sản phẩm</th>
                  <th className="border-0">Khách hàng</th>
                  <th className="border-0 text-end">Giá trị</th>
                  <th className="border-0">Trạng thái</th>
                  <th className="border-0">Ngày tạo</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map(item => {
                  const status = PENDING_STATUS_LABELS[item.status];

                  return (
                    <tr key={`${item.product}-${item.id}`}>
                      <td><Link to={ticketLink(item)} onClick={onHide}>{item.id}</Link></td>
                      <td><Link to={cartLink(item)} onClick={onHide}>{item.cartId}</Link></td>
                      <td>{itemLabel(item)}</td>
                      <td>
                        <div>{item.customerName}</div>
                        <div className="small text-muted">{item.customerPhone}</div>
                      </td>
                      <td className="text-end">{money(item.value)} ₫</td>
                      <td>
                        {status
                          ? <Badge bg={status.variant} text={status.text}>{status.label}</Badge>
                          : item.status}
                      </td>
                      <td className="small text-muted">{dateTime(item.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" className="text-gray" onClick={loadPendingOrders} disabled={loading}>Tải lại</Button>
        <Button variant="secondary" onClick={onHide}>Đóng</Button>
      </Modal.Footer>
    </Modal>
  );
};
